import { useFeatureToggle } from '~/composables/useFeatureToggle'
import {
  BOOK_NOW_COOKIE,
  parseBookNowCookie,
  resolveItemCategory4,
  resolveQuickCheckout,
  variantToBookNowCookie,
  type BookNowVariant,
  type QuickCheckoutContext,
} from '~/utils/bookNowCheckout'

/**
 * "Book Now" quick-checkout A/B test (KFT-3914).
 *
 * In the `variation` bucket a simple event booking is added to the cart and the
 * user is routed straight to `/checkout`, skipping the cart sidebar. `control`
 * keeps today's add-to-cart + sidebar behaviour.
 *
 * The bucket is set at runtime by the VWO "Book Now" campaign through the window
 * bridge (see `useBookNowExperimentBridge`). Two layers keep it correct:
 *
 * - **In-memory `useState`** is the reactive source the app reads, so if VWO
 *   fires `setVariant()` *after* the buy box has already mounted (as default),
 *   the app reacts and switches to Book Now — no timing risk.
 * - **`bookNow` cookie** is the persistence fallback, read **client-side only**:
 *   after hydration it restores the bucket so a hard navigation (product cards
 *   open event pages in a new tab) keeps the visitor in their arm. When the
 *   cookie is absent the visitor is not bucketed and behaviour is unchanged
 *   (add-to-cart, no `item_category4` tag).
 *
 * `setVariant()` writes both, so the cookie always matches what VWO assigned.
 *
 * The cookie is deliberately never read on the server. Behind CloudFront the
 * origin request policy forwards no `Cookie` header at all, so SSR could only
 * ever see it as absent; and because the HTML is cached without cookies in the
 * key, a server-read bucket would leak one visitor's arm into the page served to
 * everyone else. Consumers must therefore treat the bucket as client-only and
 * render the control experience until mounted (see `EventBuyBox`).
 *
 * Pure decision logic lives in `~/utils/bookNowCheckout`.
 */

const BOOK_NOW_STATE_KEY = 'experiment:book-now'
/**
 * One day. The cookie only has to bridge navigation within a visit — VWO
 * re-asserts the bucket on every matching page load — so a long lifetime buys
 * nothing and keeps bucketing visitors if the campaign is ever stopped. (Safari
 * caps script-written cookies at 7 days regardless.)
 */
const BOOK_NOW_COOKIE_MAX_AGE = 60 * 60 * 24
/** Local QA override so the variation can be exercised without VWO (prod-only). */
const BOOK_NOW_FEATURE_TOGGLE = 'book-now'

declare global {
  interface Window {
    kftExperiments?: {
      bookNow?: {
        setVariant: (variant: string) => void
        getVariant: () => BookNowVariant | null
        /** Present only on the head stub — calls VWO made before hydration. */
        _queue?: unknown[]
      }
      [key: string]: unknown
    }
  }
}

export const useBookNowExperiment = () => {
  const cookie = useCookie<string | null>(BOOK_NOW_COOKIE, {
    path: '/',
    sameSite: 'lax',
    maxAge: BOOK_NOW_COOKIE_MAX_AGE,
    // Store the raw value ('true'/'false') instead of Nuxt's default JSON
    // encoding, so the cookie is plainly readable ('exp-book-now-2=true').
    encode: (value) => value,
    decode: (value) => value,
  })
  const { isFeatureEnabled } = useFeatureToggle()

  // In-memory reactive source. It stays `null` on the server on purpose: behind
  // CloudFront the origin never receives the cookie, and the rendered HTML is
  // cached without cookies in the key, so a server-read bucket would be both
  // wrong (always `null`) and unsafe (one visitor's bucket baked into shared
  // HTML — the KFT-3753 failure mode, see docs/infrastructure-cloudfront.md).
  const variant = useState<BookNowVariant | null>(
    BOOK_NOW_STATE_KEY,
    () => null
  )

  // Restore the persisted bucket on the client. This cannot be done in the
  // useState initializer above: Nuxt only runs an initializer when the payload
  // slot is `undefined`, and SSR already wrote `null` into it, so the
  // initializer never runs on hydration and the cookie is never read.
  if (import.meta.client && variant.value === null) {
    variant.value = parseBookNowCookie(cookie.value)
  }

  // The QA feature toggle forces the variation locally, where VWO can't run.
  const effectiveVariant = computed<BookNowVariant | null>(() =>
    isFeatureEnabled(BOOK_NOW_FEATURE_TOGGLE) ? 'variation' : variant.value
  )

  const isVariation = computed(() => effectiveVariant.value === 'variation')

  const itemCategory4 = computed(() =>
    resolveItemCategory4(effectiveVariant.value)
  )

  const setVariant = (value: unknown): void => {
    if (value === 'control' || value === 'variation') {
      // In-memory first so the app reacts immediately even after mount, then
      // persist to the cookie so it survives navigation and matches VWO.
      variant.value = value
      cookie.value = variantToBookNowCookie(value)
    }
  }

  const shouldQuickCheckout = (ctx: QuickCheckoutContext = {}): boolean =>
    resolveQuickCheckout(isVariation.value, ctx)

  return {
    variant,
    isVariation,
    itemCategory4,
    setVariant,
    shouldQuickCheckout,
  }
}

/**
 * Inline stub for `<head>`, injected immediately **before** the VWO snippet.
 *
 * VWO loads synchronously in `<head>` and applies its campaign changes as soon
 * as settings arrive — long before Nuxt has downloaded and hydrated the client
 * bundle. A campaign calling `window.kftExperiments.bookNow.setVariant()` at
 * that point would throw ("Cannot read properties of undefined") and the visitor
 * would never be bucketed at all. This stub guarantees the API exists from the
 * first byte and buffers early calls; `useBookNowExperimentBridge` replaces it
 * on hydration and replays whatever was queued.
 *
 * `setVariant` closes over the queue rather than using `this`, so it still works
 * if a campaign grabs a detached reference to the function.
 */
export const getExperimentsBridgeStubScript = () => `
(function () {
var w = window;
w.kftExperiments = w.kftExperiments || {};
if (w.kftExperiments.bookNow) return;
var q = [];
w.kftExperiments.bookNow = {
_queue: q,
setVariant: function (v) { q.push(v); },
getVariant: function () { return null; }
};
})();
`

/**
 * Exposes `window.kftExperiments.bookNow.setVariant('control' | 'variation')` so
 * the VWO "Book Now" campaign can assign the bucket from its variation JS, and
 * drains anything the head stub buffered before hydration.
 *
 * Installed synchronously (not in `onMounted`) so the real API takes over at the
 * very start of hydration rather than after the whole tree has rendered — that
 * gap is how long the control copy stays on screen for a bucketed visitor.
 * Must be called once from a setup context (app.vue).
 */
export const useBookNowExperimentBridge = (): void => {
  const { setVariant, variant } = useBookNowExperiment()

  if (!import.meta.client || typeof window === 'undefined') return

  window.kftExperiments = window.kftExperiments || {}
  const queued = window.kftExperiments.bookNow?._queue ?? []

  window.kftExperiments.bookNow = {
    setVariant,
    getVariant: () => variant.value,
  }

  // Replay in order; the last valid assignment wins, junk is ignored by
  // setVariant just as it would be after hydration.
  for (const value of queued) setVariant(value)
}
